import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GitMerge, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const tip = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };
const SEV = { critical: "#EF4444", high: "#F59E0B", medium: "#2563EB", low: "#94A3B8" };
const RESULT_FAMILY = { pass: "green", fail: "red", blocked: "amber", not_run: "slate" };

function TraceabilitySection({ projectId }) {
  const { data: rows, isLoading } = useQuery({ queryKey: ["traceability", projectId], queryFn: () => get(`/pm/projects/${projectId}/traceability`), enabled: !!projectId });
  if (!projectId) return null;
  if (isLoading) return <Skeleton className="h-48" />;
  return (
    <Section title="Traceability Report" testid="rep-traceability">
      {!rows?.length ? <EmptyState icon={GitMerge} title="No requirement-linked epics/modules" description="Link an Epic or Module to a CRM requirement in Planning to see it here." /> : (
        <div className="divide-y divide-border">
          {rows.map((r, i) => (
            <div key={i} className="p-4 text-sm">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Requirement</div>
              <div className="font-medium">{r.requirement.description}</div>
              <div className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">{r.epic_or_module.type}</div>
              <div className="font-medium">{r.epic_or_module.title}</div>
              {r.tasks.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Tasks</div>
                  <div className="flex flex-wrap gap-1.5">{r.tasks.map((t) => <StatusBadge key={t.id} family="slate" label={t.title} />)}</div>
                </div>
              )}
              {r.test_cases.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Test Cases</div>
                  <div className="flex flex-wrap gap-1.5">{r.test_cases.map((tc, ti) => <StatusBadge key={ti} family={RESULT_FAMILY[tc.status]} label={`${tc.title}: ${tc.status}`} />)}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function HoursReportSection({ projectId }) {
  const { data: rows, isLoading } = useQuery({ queryKey: ["hours-report", projectId], queryFn: () => get(`/pm/projects/${projectId}/hours-report`), enabled: !!projectId });
  if (!projectId) return null;
  if (isLoading) return <Skeleton className="h-48" />;
  return (
    <Section title="Estimated vs Actual Hours" testid="rep-hours">
      {!rows?.length ? <EmptyState icon={Clock} title="No tasks on this project yet" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Task</th>
              <th className="px-4 py-3 font-semibold">Assignee</th>
              <th className="px-3 py-3 text-right font-semibold">Estimated</th>
              <th className="px-3 py-3 text-right font-semibold">Actual</th>
              <th className="px-3 py-3 text-right font-semibold">Difference</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.item_id} data-testid={`hours-row-${r.item_id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><span className="font-medium">{r.title}</span> <span className="text-xs text-muted-foreground">({r.type})</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{r.assignee_name || "Unassigned"}</td>
                  <td className="px-3 py-3 text-right font-mono">{r.estimated_hours}h</td>
                  <td className="px-3 py-3 text-right font-mono">{r.actual_hours}h</td>
                  <td className={cn("px-3 py-3 text-right font-mono font-semibold",
                    r.diff > 0 ? "text-red-600 dark:text-red-400" : r.diff < 0 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
                    {r.diff > 0 ? "+" : ""}{r.diff}h
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

export default function PmReports() {
  const outletCtx = useOutletContext();
  const [pickedProject, setPickedProject] = useState("");
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => get("/pm/projects"), enabled: !outletCtx?.project });
  const { data, isLoading } = useQuery({ queryKey: ["pm-reports"], queryFn: () => get("/pm/reports") });
  const traceabilityProjectId = outletCtx?.project?.id || pickedProject;
  if (isLoading || !data) return <div className="grid grid-cols-2 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72" />)}</div>;

  return (
    <div>
      <PageHeader title="PM Reports" subtitle="Delivery metrics across projects.">
        {!outletCtx?.project && (
          <Select value={pickedProject} onValueChange={setPickedProject}>
            <SelectTrigger className="w-56" data-testid="rep-project-picker"><SelectValue placeholder="Pick a project for Traceability & Hours" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </PageHeader>
      <div className="mb-6"><TraceabilitySection projectId={traceabilityProjectId} /></div>
      <div className="mb-6"><HoursReportSection projectId={traceabilityProjectId} /></div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Sprint Burndown" testid="rep-burndown">
          <div className="p-4" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.burndown}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} />
                <Line type="monotone" dataKey="remaining" stroke="#2563EB" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Velocity Trend" testid="rep-velocity">
          <div className="p-4" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.velocity}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="sprint" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tip} />
                <Bar dataKey="points" fill="#22C55E" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Bug Severity Distribution" testid="rep-bugs">
          <div className="p-4" style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.bug_severity} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
                  {data.bug_severity.map((e, i) => <Cell key={i} fill={SEV[e.name] || "#94A3B8"} />)}
                </Pie>
                <Tooltip contentStyle={tip} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              {data.bug_severity.map((s) => <span key={s.name} className="flex items-center gap-1.5 text-xs capitalize text-muted-foreground"><span className="h-2 w-2 rounded-full" style={{ background: SEV[s.name] }} />{s.name}</span>)}
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
